import { saveAs } from 'file-saver';
import {
  appendUnderwritingCalculatorSheet,
} from './cloneExcelWorksheet';
import {
  getCohortRoasAvgWindows,
  getCohortSummary,
  maxMobForCohortSheet,
} from './cohortSheetSummary';
import {
  findPeriodByMob,
  getCohortPeriodValue,
} from './cohortSheetPeriodValue';
import { EXCEL_THEME, MONEY_NUM_FMT, PCT_NUM_FMT } from './excelTheme';
import { buildUwScoringRows } from './underwritingScorecardScoring';

export const COHORT_SHEET_SUMMARY_TAB = 'Summary';
export const COHORT_SHEET_SUMMARY_TAB_MERGED = 'Cohort Summary';
export const QUICK_CASHFLOW_SUMMARY_TAB_MERGED = 'Cashflow Summary';
export const COHORT_SHEET_UNDERWRITING_TAB = 'Underwriting Calculator';

export const COHORT_SHEET_TABLE_GAP_ROWS = 2;

export const COHORT_SHEET_DATA_TAB_NAMES = ['ROAS', 'Revenue'];

export const COHORT_SHEET_TAB_NAMES = [
  COHORT_SHEET_UNDERWRITING_TAB,
  COHORT_SHEET_SUMMARY_TAB,
  ...COHORT_SHEET_DATA_TAB_NAMES,
];

export const COHORT_SHEET_EXPORT_SPECS = [
  { tab: COHORT_SHEET_DATA_TAB_NAMES[0], metric: 'roas' },
  { tab: COHORT_SHEET_DATA_TAB_NAMES[1], metric: 'revenue' },
];

function styleBrandHeader(cell) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.brand },
  };
  cell.font = { bold: true, color: { argb: EXCEL_THEME.brandText }, size: 11 };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
}

function stylePredictedCell(cell) {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_THEME.predictedFill },
  };
}

function writeCohortTableSectionTitle(worksheet, row, title) {
  worksheet.getCell(row, 1).value = title;
  worksheet.getCell(row, 1).font = { bold: true, size: 12 };
  return row + 1;
}

function getCohortTableSectionTitle(metric, periodType) {
  const label = metric === 'roas' ? 'ROAS' : 'Revenue';
  return `${label} ${periodType}`;
}

function writeCohortTable(worksheet, cohortSheet, maxMob, metric, periodType, startRow) {
  const headerRow = startRow;
  styleBrandHeader(worksheet.getCell(headerRow, 1));
  worksheet.getCell(headerRow, 1).value = 'Cohort date';
  styleBrandHeader(worksheet.getCell(headerRow, 2));
  worksheet.getCell(headerRow, 2).value = 'Marketing spend';

  for (let mob = 0; mob < maxMob; mob += 1) {
    const cell = worksheet.getCell(headerRow, 3 + mob);
    styleBrandHeader(cell);
    cell.value = `M${mob}`;
  }

  cohortSheet.cohorts.forEach((row, rowIndex) => {
    const sheetRow = headerRow + 1 + rowIndex;
    worksheet.getCell(sheetRow, 1).value = row.cohortDate;
    const spendCell = worksheet.getCell(sheetRow, 2);
    spendCell.value = Number(row.marketingSpend) || 0;
    spendCell.numFmt = MONEY_NUM_FMT;

    for (let mob = 0; mob < maxMob; mob += 1) {
      const period = findPeriodByMob(row.periods, mob);
      if (!period) {
        continue;
      }
      const prevPeriod = mob > 0 ? findPeriodByMob(row.periods, mob - 1) : null;
      const value = getCohortPeriodValue(
        period,
        prevPeriod,
        metric,
        periodType
      );
      const cell = worksheet.getCell(sheetRow, 3 + mob);
      if (metric === 'roas') {
        cell.value = value;
        cell.numFmt = PCT_NUM_FMT;
      } else {
        cell.value = value;
        cell.numFmt = MONEY_NUM_FMT;
      }
      if (period.isPredicted) {
        stylePredictedCell(cell);
      }
    }
  });

  return headerRow + cohortSheet.cohorts.length;
}

function applyCohortDataSheetLayout(worksheet, maxMob) {
  worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 2, activeCell: 'C2' }];
  worksheet.getColumn(1).width = 14;
  worksheet.getColumn(2).width = 16;
  for (let mob = 0; mob < maxMob; mob += 1) {
    worksheet.getColumn(3 + mob).width = 12;
  }
}

function writeCombinedCohortMetricSheet(worksheet, cohortSheet, maxMob, metric) {
  let row = 1;
  row = writeCohortTableSectionTitle(
    worksheet,
    row,
    getCohortTableSectionTitle(metric, 'cumulative')
  );
  const cumulativeEndRow = writeCohortTable(
    worksheet,
    cohortSheet,
    maxMob,
    metric,
    'cumulative',
    row
  );
  row = cumulativeEndRow + 1 + COHORT_SHEET_TABLE_GAP_ROWS;
  row = writeCohortTableSectionTitle(
    worksheet,
    row,
    getCohortTableSectionTitle(metric, 'incremental')
  );
  writeCohortTable(
    worksheet,
    cohortSheet,
    maxMob,
    metric,
    'incremental',
    row
  );
  applyCohortDataSheetLayout(worksheet, maxMob);
}

function writeSummarySheet(worksheet, cohortSheet, maxMob) {
  const summary = getCohortSummary(cohortSheet);
  let row = 1;

  worksheet.getCell(row, 1).value =
    'Generic ROAS vector (3-cohort avg, cumulative)';
  worksheet.getCell(row, 1).font = { bold: true, size: 12 };
  row += 1;

  styleBrandHeader(worksheet.getCell(row, 1));
  worksheet.getCell(row, 1).value = 'MOB';
  for (let mob = 0; mob < maxMob; mob += 1) {
    const headerCell = worksheet.getCell(row, 2 + mob);
    styleBrandHeader(headerCell);
    headerCell.value = `M${mob}`;
  }
  row += 1;

  worksheet.getCell(row, 1).value = 'ROAS %';
  for (let mob = 0; mob < maxMob; mob += 1) {
    const valueCell = worksheet.getCell(row, 2 + mob);
    const pct = summary.genericRoasVectorPct[mob];
    if (pct != null && pct !== 0) {
      valueCell.value = pct / 100;
      valueCell.numFmt = PCT_NUM_FMT;
    }
  }
  row += 2;

  worksheet.getCell(row, 1).value =
    'Predicted runoff revenue (incremental diagonal)';
  worksheet.getCell(row, 1).font = { bold: true, size: 12 };
  row += 1;

  const anchorLabel = summary.runoff.anchorCohortDate
    ? `Anchor cohort: ${summary.runoff.anchorCohortDate}, first predicted MOB: M${summary.runoff.firstPredictedMob ?? '—'}`
    : 'No predicted MOBs on the latest cohort row';
  worksheet.getCell(row, 1).value = anchorLabel;
  row += 1;

  if (summary.runoff.runoffByMob.length > 0) {
    styleBrandHeader(worksheet.getCell(row, 1));
    worksheet.getCell(row, 1).value = 'MOB';
    summary.runoff.runoffByMob.forEach((entry, index) => {
      const headerCell = worksheet.getCell(row, 2 + index);
      styleBrandHeader(headerCell);
      headerCell.value = `M${entry.mob}`;
    });
    row += 1;

    worksheet.getCell(row, 1).value = 'Runoff $';
    summary.runoff.runoffByMob.forEach((entry, index) => {
      const valueCell = worksheet.getCell(row, 2 + index);
      valueCell.value = entry.revenue;
      valueCell.numFmt = MONEY_NUM_FMT;
    });
    row += 1;

    worksheet.getCell(row, 1).value = 'Cumulative runoff $';
    let cumulativeRunoff = 0;
    summary.runoff.runoffByMob.forEach((entry, index) => {
      cumulativeRunoff += Number(entry.revenue) || 0;
      const valueCell = worksheet.getCell(row, 2 + index);
      valueCell.value = cumulativeRunoff;
      valueCell.numFmt = MONEY_NUM_FMT;
    });
    row += 2;
  } else {
    row += 1;
  }

  worksheet.getCell(row, 1).value = 'Predicted runoff total';
  worksheet.getCell(row, 1).font = { bold: true, size: 12 };
  row += 1;
  const totalCell = worksheet.getCell(row, 1);
  totalCell.value = summary.runoffTotal;
  totalCell.numFmt = MONEY_NUM_FMT;
  totalCell.font = { bold: true };
  row += 2;

  if (summary.runoff.runoffByMob.length > 0) {
    worksheet.getCell(row, 1).value = 'Predicted runoff 12 months';
    worksheet.getCell(row, 1).font = { bold: true, size: 12 };
    row += 1;
    const runoff12Cell = worksheet.getCell(row, 1);
    runoff12Cell.value = summary.runoff12Months;
    runoff12Cell.numFmt = MONEY_NUM_FMT;
    runoff12Cell.font = { bold: true };
    row += 2;

    worksheet.getCell(row, 1).value = 'Predicted runoff 24 months';
    worksheet.getCell(row, 1).font = { bold: true, size: 12 };
    row += 1;
    const runoff24Cell = worksheet.getCell(row, 1);
    runoff24Cell.value = summary.runoff24Months;
    runoff24Cell.numFmt = MONEY_NUM_FMT;
    runoff24Cell.font = { bold: true };
    row += 2;
  }

  const avgWindows = getCohortRoasAvgWindows(cohortSheet);
  if (avgWindows.length > 0) {
    row += 1;
    worksheet.getCell(row, 1).value =
      'Trailing cohort ROAS averages (Avg last 3–12, capped at available cohorts)';
    worksheet.getCell(row, 1).font = { bold: true, size: 12 };
    row += 1;

    styleBrandHeader(worksheet.getCell(row, 1));
    worksheet.getCell(row, 1).value = 'Window';
    styleBrandHeader(worksheet.getCell(row, 2));
    worksheet.getCell(row, 2).value = 'Avg stdev';
    for (let mob = 0; mob < maxMob; mob += 1) {
      const headerCell = worksheet.getCell(row, 3 + mob);
      styleBrandHeader(headerCell);
      headerCell.value = `M${mob}`;
    }
    row += 1;

    avgWindows.forEach((windowStats) => {
      worksheet.getCell(row, 1).value = `Avg last ${windowStats.avgCohorts}`;
      const stdevCell = worksheet.getCell(row, 2);
      if (windowStats.avgStdevPct != null) {
        stdevCell.value = windowStats.avgStdevPct / 100;
        stdevCell.numFmt = PCT_NUM_FMT;
      }
      for (let mob = 0; mob < maxMob; mob += 1) {
        const valueCell = worksheet.getCell(row, 3 + mob);
        const pct = windowStats.avgVectorPct[mob];
        if (pct != null && pct !== 0) {
          valueCell.value = pct / 100;
          valueCell.numFmt = PCT_NUM_FMT;
        }
      }
      row += 1;
    });
  }

  worksheet.getColumn(1).width = 42;
  worksheet.getColumn(2).width = 12;
  for (let mob = 0; mob < maxMob; mob += 1) {
    worksheet.getColumn(3 + mob).width = 12;
  }
}

export async function appendCohortExportSheets(workbook, cohortSheet, options = {}) {
  if (!cohortSheet?.cohorts?.length) {
    return false;
  }

  const ExcelJS = options.ExcelJS ?? (await import('exceljs')).default;
  const maxMob = maxMobForCohortSheet(cohortSheet);
  const summaryTabName = options.summaryTabName ?? COHORT_SHEET_SUMMARY_TAB;

  await appendUnderwritingCalculatorSheet(workbook, ExcelJS, {
    ...options,
    monthlyOpex: options.monthlyOpex,
    scorecardConfig: options.scorecardConfig ?? null,
    scoringRows: options.scorecardConfig
      ? buildUwScoringRows({
          cohortSheet,
          monthlyOpex: options.monthlyOpex,
          beginCash: options.beginCash ?? 0,
          netBurn: options.netBurn ?? 0,
          runway: options.runway ?? null,
          runwaySource: options.runwaySource ?? 'Default',
          predictedRunoff: options.predictedRunoff ?? null,
          predictedRunoffSource: options.predictedRunoffSource ?? 'Default',
          lastMarketingSpend: options.lastMarketingSpend ?? 0,
          maxMarketingRoas100: options.maxMarketingRoas100 ?? 0,
          debt: options.debt ?? 0,
          totalAssets: options.totalAssets ?? 0,
          totalAssetsSource: options.totalAssetsSource ?? 'Default',
          totalLiabilities: options.totalLiabilities ?? 0,
          totalLiabilitiesSource: options.totalLiabilitiesSource ?? 'Default',
          safe: options.safe ?? 0,
          revenueGrowthYoy: options.revenueGrowthYoy ?? null,
          revenueGrowthYoySource: options.revenueGrowthYoySource ?? 'Default',
          hillModel: options.hillModel ?? null,
          scorecardConfig: options.scorecardConfig,
          accountingSnapshot: options.accountingSnapshot ?? null,
          investorScore: options.investorScore ?? null,
          managementScore: options.managementScore ?? null,
          stressTestingScore: options.stressTestingScore ?? null,
        })
      : null,
  });

  const summaryWorksheet = workbook.addWorksheet(summaryTabName);
  writeSummarySheet(summaryWorksheet, cohortSheet, maxMob);

  COHORT_SHEET_EXPORT_SPECS.forEach(({ tab, metric }) => {
    const worksheet = workbook.addWorksheet(tab);
    writeCombinedCohortMetricSheet(worksheet, cohortSheet, maxMob, metric);
  });

  return true;
}

export async function buildCohortSheetWorkbook(cohortSheet, options = {}) {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Twelve Cockpit';
  workbook.created = new Date();

  const appended = await appendCohortExportSheets(workbook, cohortSheet, {
    ...options,
    ExcelJS,
  });
  if (!appended) {
    return null;
  }

  return workbook;
}

export async function downloadCohortSheetExcel(
  cohortSheet,
  filenameBase = 'quick-cashflow',
  options = {}
) {
  const workbook = await buildCohortSheetWorkbook(cohortSheet, options);
  if (!workbook) {
    return;
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const safeBase = String(filenameBase).replace(/[^\w.-]+/g, '-') || 'quick-cashflow';
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${safeBase}-cohort-data.xlsx`
  );
}
